import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Mounts `children` only once its sentinel enters (or nearly enters) the
 * viewport. Preferred over requestIdleCallback for below-fold sections
 * because it keeps hero-LCP work uncontested even on slow devices where
 * the browser reports "idle" while the hero image is still decoding.
 *
 *  - Uses IntersectionObserver with a configurable rootMargin so the chunk
 *    starts streaming a bit *before* the user scrolls it into view.
 *  - Optional `prefetch` callback is fired at a larger `prefetchRootMargin`
 *    so the lazy chunk's JS starts downloading well before we actually
 *    mount it — makes scrolling feel instant on mid-range devices.
 *  - Reserves layout space via `minHeight` on the sentinel to prevent CLS.
 *  - Falls back to an immediate mount when IO is unavailable (very old
 *    browsers, SSR/prerender pass) so content never becomes invisible.
 */
type Props = {
  children: ReactNode;
  /** Distance ahead of the viewport at which to trigger the mount. */
  rootMargin?: string;
  /** Reserved layout height (px or CSS length) to avoid CLS. */
  minHeight?: number | string;
  /** Fallback: mount anyway after this many ms if IO never fires. */
  fallbackTimeoutMs?: number;
  /**
   * Fired once when the sentinel is within `prefetchRootMargin` of the
   * viewport. Use it to warm the dynamic import for the lazy child, e.g.
   *   prefetch={() => import('@/pages/foo/FooBelowFold')}
   */
  prefetch?: () => Promise<unknown> | void;
  /** How far ahead of the viewport to fire `prefetch`. Larger than `rootMargin`. */
  prefetchRootMargin?: string;
};

export function MountOnVisible({
  children,
  rootMargin = '600px 0px',
  minHeight = '50vh',
  fallbackTimeoutMs = 4000,
  prefetch,
  prefetchRootMargin = '1500px 0px',
}: Props) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (mounted) return;

    // Prerender / very old browsers: mount immediately so content is present.
    if (typeof IntersectionObserver === 'undefined') {
      setMounted(true);
      return;
    }

    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setMounted(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold: 0 }
    );
    observer.observe(node);

    // Warm the lazy chunk earlier than the mount trigger so the JS is
    // parsed/executed by the time the user actually reaches the section.
    let prefetchObserver: IntersectionObserver | null = null;
    if (prefetch) {
      let prefetched = false;
      const run = () => {
        if (prefetched) return;
        prefetched = true;
        try {
          void prefetch();
        } catch {
          /* swallow — prefetch failures shouldn't break the page */
        }
      };
      prefetchObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            run();
            prefetchObserver?.disconnect();
            prefetchObserver = null;
          }
        },
        { rootMargin: prefetchRootMargin, threshold: 0 }
      );
      prefetchObserver.observe(node);
    }

    // Safety net: if the user never scrolls (short page, tab hidden, etc.)
    // still mount after a few seconds so JSON-LD/footer/etc. exist in the DOM
    // for crawlers and copy-link users.
    const t = window.setTimeout(() => setMounted(true), fallbackTimeoutMs);

    return () => {
      observer.disconnect();
      prefetchObserver?.disconnect();
      window.clearTimeout(t);
    };
  }, [mounted, rootMargin, prefetchRootMargin, prefetch, fallbackTimeoutMs]);

  if (mounted) return <>{children}</>;
  return <div ref={sentinelRef} aria-hidden="true" style={{ minHeight }} />;
}
